import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage, isApiErrorHandled } from "@/services/api";
import { toast } from "sonner";
import AuthLayout from "@/components/layout/AuthLayout";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { sendSignupOtpService } from "@/services/auth.service";
import { FormInput } from "@/components/forms/FormControls";
import { loginSchema, registerSchema, type RegisterValues } from "@/schemas/auth";
import { PUBLIC_ROUTES } from "@/routes/url";
import { UserRole } from "@/enum/common.enum";

interface AuthFormProps {
  mode?: "login" | "register";
}

export default function AuthForm({ mode = "login" }: AuthFormProps) {
  const isRegister = mode === "register";
  const { login, register, user, context } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const form = useForm<RegisterValues>({
    resolver: zodResolver(isRegister ? registerSchema : loginSchema) as never,
    defaultValues: { name: "", email: "", password: "", role: UserRole.AGENT },
  });
  const submit = async (values: RegisterValues) => {
    setBusy(true);
    try {
      if (isRegister && !otpSent) {
        await sendSignupOtpService(values.email);
        setOtpSent(true);
        toast.success(`We sent a 6-digit code to ${values.email}`);
        return;
      }
      if (isRegister) {
        await register({ ...values, role: UserRole.AGENT, otp });
        toast.success("Welcome to Stoop");
      } else {
        await login(values.email, values.password);
        toast.success("Welcome back");
      }
      navigate(PUBLIC_ROUTES.OVERVIEW, { replace: true });
    } catch (err) {
      if (!isApiErrorHandled(err)) {
        toast.error(apiErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  if (user) {
    return <Navigate to={context?.landing_route || PUBLIC_ROUTES.OVERVIEW} replace />;
  }

  return (
    <AuthLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(submit)} data-testid="auth-form" noValidate>
          <h1 className="font-display text-4xl tracking-tight text-center">{isRegister ? "Open your studio." : "Welcome back."}</h1>
          <p className="text-stone mt-3 text-sm text-center">{isRegister ? "Three minutes to set up. Cancel anytime." : "Sign in to your client portal."}</p>

          <div className="mt-8 flex flex-col gap-3">
            {isRegister && (
              <FormInput name="name" label="Full name" placeholder="Enter your full name" autoComplete="name" data-testid="auth-name" required />
            )}
            <FormInput name="email" label="Email" type="email" placeholder="Enter your email" autoComplete="email" data-testid="auth-email" required />
            <div>
              <FormInput name="password" label="Password" type="password" placeholder={isRegister ? "Create a password" : "Enter your password"} autoComplete={isRegister ? "new-password" : "current-password"} data-testid="auth-password" required />
              {!isRegister && (
                <div className="mt-2 text-right">
                  <Link to={PUBLIC_ROUTES.FORGOT_PASSWORD} className="text-xs text-stone hover:text-moss underline underline-offset-2" data-testid="auth-forgot-link">
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>
            {isRegister && otpSent && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="auth-otp" className="text-sm font-medium">Verification code</label>
                <Input id="auth-otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code from your email" data-testid="auth-otp" autoFocus />
                <button type="button" className="text-xs text-stone hover:text-moss underline underline-offset-2 self-end" onClick={() => { setOtpSent(false); setOtp(""); }}>
                  Change email or resend code
                </button>
              </div>
            )}
          </div>

          <Button type="submit" variant="dashboardPrimary" size="lg" loading={busy} disabled={isRegister && otpSent && otp.length !== 6} className="mt-8 w-full" data-testid="auth-submit">
            {isRegister ? (otpSent ? "Sign up" : "Continue") : "Sign in"}
          </Button>

          {!isRegister && (
            <>
              <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-widest text-stone" aria-hidden="true">
                <span className="h-px flex-1 bg-edge" />or<span className="h-px flex-1 bg-edge" />
              </div>
              <Button asChild variant="dashboardOutline" size="lg" className="w-full border-primary text-primary hover:text-primary">
                <Link to={PUBLIC_ROUTES.LOGIN_PHONE} data-testid="auth-phone-login-link">Sign in with a text code</Link>
              </Button>
            </>
          )}

          <div className="mt-6 text-sm text-stone text-center">
            {isRegister ? (
              <>Already have an account? <Link to={PUBLIC_ROUTES.LOGIN} className="text-moss underline underline-offset-4" data-testid="auth-switch">Sign in</Link></>
            ) : (
              <>Don&apos;t have an account? <Link to={PUBLIC_ROUTES.REGISTER} className="text-moss underline underline-offset-4" data-testid="auth-switch">Sign up</Link></>
            )}
          </div>
        </form>
      </Form>
    </AuthLayout>
  );
}
